
import { RevButton } from '@ds/components/Button'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevModal } from '@ds/components/Modal'
import { closeModal } from '@ds/components/ModalBase'
import { RevToast } from '@ds/components/Toast'
import isEmpty from 'lodash/isEmpty'

import Categories from '@buyback/customer/swap/components/Categories'
import NoOffer from '@buyback/customer/swap/components/NoOffer'
import Offer from '@buyback/customer/swap/components/Offer'
import {
  STATUS_CODE_NO_OFFER,
  SWAP_ERROR_MESSAGE,
} from '@buyback/customer/swap/components/SwapModal/constants'
import QuestionsForm, {
  QUESTIONS_FORM_ID,
} from '@buyback/shared/components/QuestionsForm'
import { TOAST_VARIANTS } from '@buyback/shared/constants'
import modalTranslations from '@buyback/shared/translations/Modal.translations'
import toastTranslations from '@buyback/shared/translations/Toast.translations'
import { MODAL_NAMES } from '@config/constants'
import { TOAST_BUYBACK_SWAP_ERROR } from '@config/constants/toasts'
import { hashObjects } from '@core/helpers'
import {
  addSwapToCart,
  getBuybackOffer,
  getBuybackSwapQuestions,
  postBuybackAnswers,
} from '@http/endpoints'
import logger from '@logger'
import { ROUTES } from '@router'
import { trackSwapClick, trackSwapModal } from '@tracking/events'

import translations from './SwapModal.translations'

const STEPS = {
  CATEGORIES: 'categories',
  QUESTIONS: 'questions',
  OFFER: 'offer',
  NO_OFFER: 'no_offer',
}

export default {
  components: {
    Offer,
    NoOffer,
    RevModal,
    QuestionsForm,
    Categories,
    RevButton,
    RevToast,
    RevLoadingScreen,
  },
  props: {
    modalName: {
      type: String,
      required: true,
    },
    redirectOnCloseTarget: {
      type: String,
      default: null,
    },
    datalayerCategory: {
      type: String,
      default: '',
    },
    swapListings: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      offer: {},
      funnel: [],
      categories: {},
      isLoadingCategories: false,
      isPostingAnswers: false,
      form: {},
      history: {},
      funnelButtonLabel: '',
      showFunnelButton: false,
      scrollToTop: false,
      step: STEPS.CATEGORIES,
      trackingEnabled: true,
      toast: {
        opened: false,
        title: '',
        message: '',
        variant: TOAST_VARIANTS.NEUTRAL,
        name: TOAST_BUYBACK_SWAP_ERROR,
        iconSrc: '',
      },
    }
  },
  computed: {
    QUESTIONS_FORM_ID: () => QUESTIONS_FORM_ID,
    translations: () => translations,
    modalTranslations: () => modalTranslations,
    toastTranslations: () => toastTranslations,
    displayIntro() {
      return this.redirectOnCloseTarget !== null
    },
    displayLoader() {
      return this.isLoadingCategories || this.isPostingAnswers
    },
    loadingText() {
      return this.isPostingAnswers
        ? [this.$t(translations.loadingOfferText)]
        : []
    },
    displayCategories() {
      return this.step === STEPS.CATEGORIES && !this.isLoadingCategories
    },
    displayFunnel() {
      return this.step === STEPS.QUESTIONS
    },
    displayNoOffer() {
      return this.step === STEPS.NO_OFFER
    },
    displayOffer() {
      return this.step === STEPS.OFFER
    },
    showBackButton() {
      return (
        this.step === STEPS.QUESTIONS ||
        this.step === STEPS.OFFER ||
        this.step === STEPS.NO_OFFER
      )
    },
    dismissButtonVariant() {
      return this.step === STEPS.CATEGORIES && this.redirectOnCloseTarget
        ? 'secondary'
        : 'primary'
    },
    showDismissButton() {
      return (
        this.step === STEPS.NO_OFFER ||
        (this.step === STEPS.CATEGORIES && this.redirectOnCloseTarget)
      )
    },
    dismissButtonList() {
      return {
        [MODAL_NAMES.BUYBACK.SWAP_ATC]: {
          label: this.$t(translations.goToCartButtonLabel),
          trackingPayload: {
            action: this.displayIntro ? 'funnel > Step 1' : 'funnel',
            label: 'go_to_cart',
          },
        },
        [MODAL_NAMES.BUYBACK.SWAP_CART]: {
          label: this.$t(translations.noFromCart),
          trackingPayload: {
            action: 'funnel',
            label: 'back_to_cart',
          },
        },
        [MODAL_NAMES.BUYBACK.SWAP_CHECKOUT_SUMMARY]: {
          label: this.$t(translations.noFromCart),
          trackingPayload: {
            action: 'funnel',
            label: 'back_to_cart',
          },
        },
        [MODAL_NAMES.BUYBACK.SWAP_PRODUCT]: {
          label: this.$t(translations.noFromProduct),
          trackingPayload: {
            action: 'funnel',
            label: 'back_to_product',
          },
        },
      }
    },
    dismissButton() {
      return this.dismissButtonList[this.modalName]
    },
    questionsSteps() {
      return (
        this.funnel.map(({ step }, index) => {
          return {
            index,
            label: step.label,
            name: step.key,
            completed: step.completed,
            active: step.active,
            infoBlock: step.infoBlock,
          }
        }) || []
      )
    },
    activeQuestionsStep() {
      return this.questionsSteps.find((step) => step.active) || {}
    },
    titles() {
      return {
        [STEPS.CATEGORIES]: this.$t(translations.categoriesTitle),
        [STEPS.QUESTIONS]: this.$t(translations.title, {
          stepNumber: this.activeQuestionsStep.index + 1,
          totalStep: this.funnel.length,
          label: this.activeQuestionsStep.label,
        }),
        [STEPS.NO_OFFER]: this.$t(translations.noOfferTitle),
        [STEPS.OFFER]: this.$t(translations.offerTitle),
      }
    },
    title() {
      return this.titles[this.step]
    },
  },
  methods: {
    handleOpen() {
      this.handleTracking({
        action: 'modal',
        label: 'swap',
      })
      this.getCategories({ tracking: false })
    },
    async getQuestions({ form = {}, trackingKey = '' } = {}) {
      this.handleTracking({
        action: 'funnel > Step 2',
        label: trackingKey,
      })

      const id = hashObjects(form)
      const inMemoryPayload = this.history[id]

      if (inMemoryPayload) {
        return inMemoryPayload
      }

      const { payload } = await this.$store.dispatch('http/request', {
        request: getBuybackSwapQuestions,
        queryParams: {
          ...this.form,
        },
      })

      this.history[id] = payload

      return payload
    },
    async getCategories({ tracking = true } = {}) {
      if (tracking) {
        this.handleTracking({
          action: 'funnel > Step 1',
          label: 'buyback_estimate',
        })
      }

      this.isLoadingCategories = true

      try {
        const payload = await this.getQuestions()
        const [categories] = payload.funnel

        this.categories = categories.questions[0]
        this.step = STEPS.CATEGORIES
      } catch (error) {
        logger.error(SWAP_ERROR_MESSAGE.GET_CATEGORIES, error)
        this.openErrorToast()
      } finally {
        this.isLoadingCategories = false
      }
    },
    async getCategoryFunnel(category) {
      try {
        this.form = {
          ...category,
        }
        const payload = await this.getQuestions({
          form: this.form,
          trackingKey: 'category',
        })

        this.funnel = payload.funnel
        this.step = STEPS.QUESTIONS
      } catch (error) {
        logger.error(SWAP_ERROR_MESSAGE.GET_QUESTIONS, error)
        this.openErrorToast()
      } finally {
        // see if to put loader
      }
    },
    async getNextQuestion({ form = {}, reset = [], trackingKey }) {
      try {
        this.form = {
          ...this.form,
          ...form,
        }

        if (!isEmpty(reset)) {
          reset.forEach((key) => {
            delete this.form[key]
          })
        }

        const payload = await this.getQuestions({
          form: this.form,
          trackingKey,
        })
        this.funnel = payload.funnel

        await this.$nextTick()
        this.$refs.bottom.scrollIntoView({
          block: 'end',
          inline: 'nearest',
          behavior: 'smooth',
        })
      } catch (error) {
        logger.error(SWAP_ERROR_MESSAGE.GET_QUESTIONS, error)
        this.openErrorToast()
      }
    },
    async getNextStep({ form, scrollToTop, trackingKey }) {
      this.scrollToTop = scrollToTop

      await this.getNextQuestion({ form, trackingKey })

      if (this.scrollToTop) {
        this.$refs.container.$el.scrollIntoView(true)
        this.scrollToTop = false
      }
    },
    async handleShowFunnelButton({ label, visible, scrollBottom }) {
      this.funnelButtonLabel = label
      this.showFunnelButton = visible

      if (scrollBottom) {
        await this.$nextTick()
        this.$refs.bottom.scrollIntoView({
          block: 'end',
          inline: 'nearest',
          behavior: 'smooth',
        })
      }
    },
    async getFunnelStepFromHistory() {
      const isFirstQuestionStep =
        this.funnel.findIndex(({ step }) => step.active) === 0
      if (isFirstQuestionStep) {
        this.step = STEPS.CATEGORIES
        this.showFunnelButton = false

        return
      }

      try {
        const activeQuestionsStep = this.funnel.find(({ step }) => step.active)
        const keysToDelete = activeQuestionsStep.questions.map(
          (question) => question.key,
        )

        keysToDelete.forEach((key) => {
          delete this.form[key]
        })
        this.form = {
          ...this.form,
          nextStep: false,
        }
        const payload = await this.getQuestions({ form: this.form })
        this.funnel = payload.funnel
      } catch (error) {
        logger.error(SWAP_ERROR_MESSAGE.GET_QUESTIONS, error)
        this.openErrorToast()
      }
    },
    async handleBack() {
      if (this.step === STEPS.QUESTIONS) {
        await this.getFunnelStepFromHistory()
      }

      if (this.step === STEPS.OFFER || this.step === STEPS.NO_OFFER) {
        this.step = STEPS.QUESTIONS
        this.showFunnelButton = true
      }
    },
    async handlePostAnswers({ trackingKey, form }) {
      try {
        this.form = {
          ...this.form,
          ...form,
        }
        this.isPostingAnswers = true
        this.showFunnelButton = false

        // Wait 800 before fetching the offer in order to
        // give customers the feeling that we are 'really' searching
        // (meanwhile a screen loader with additional information is shown)
        await new Promise((resolve) => {
          setTimeout(resolve, 800)
        })

        this.handleTracking({
          action: 'funnel > Step 2',
          label: trackingKey,
        })

        this.handleTracking({
          action: 'funnel > Step 2',
          label: 'see_estimate',
        })

        const { status_code: statusCode, payload } = await this.postAnswers(
          this.form,
        )

        if (statusCode === STATUS_CODE_NO_OFFER) {
          this.step = STEPS.NO_OFFER
          this.handleTracking({
            action: 'funnel > Step 2',
            label: 'scarabee',
          })
        } else {
          const { payload: offerPayload } = await this.getOffer(
            payload.sourcingListingId,
          )
          this.step = STEPS.OFFER
          this.offer = offerPayload
        }
      } catch (error) {
        logger.error(SWAP_ERROR_MESSAGE.GET_OFFER, error)
        this.openErrorToast()
        this.showFunnelButton = true
      } finally {
        this.isPostingAnswers = false
      }
    },
    async postAnswers(answers) {
      return this.$store.dispatch('http/request', {
        request: postBuybackAnswers,
        queryParams: {
          swap: true,
        },
        body: {
          ...answers,
        },
      })
    },
    async getOffer(sourcingListingId) {
      return this.$store.dispatch('http/request', {
        request: getBuybackOffer,
        pathParams: {
          sourcingListingId,
        },
      })
    },
    async handleClose() {
      // dismissal should not be tracked if user got to the offer view
      if (this.step !== STEPS.OFFER && this.trackingEnabled) {
        this.handleTracking({
          ...this.dismissButton.trackingPayload,
        })
      }

      if (this.step === STEPS.OFFER && this.trackingEnabled) {
        this.handleTracking({
          action: 'funnel > Step 3',
          label: 'decline_offer',
        })
      }
      // If on product page call again estimation endpoint
      // to update page state and do not display again the modal

      if (
        this.$route.name === ROUTES.PRODUCT.HOME &&
        this.step === STEPS.OFFER &&
        !this.redirectOnCloseTarget
      ) {
        try {
          await this.$store.dispatch(
            'swap/getSwapEstimation',
            this.swapListings,
          )
        } catch {
          // fails silently
        }
      }

      if (this.redirectOnCloseTarget === ROUTES.CHECKOUT.CART) {
        this.$emit('continue')
      }

      if (this.step === STEPS.OFFER) {
        this.$emit('confirmation')
      }

      this.resetFunnel()
    },
    handleToastClose() {
      this.toast.opened = false
    },
    async handleDismiss() {
      // TODO BBACK-1053 Move away from vuex
      await this.$store.dispatch('swap/disableSwap')

      closeModal(this.modalName)
    },
    async handleAcceptOffer() {
      try {
        this.handleTracking({
          action: 'funnel > Step 3',
          label: 'accept_offer',
        })
        await this.$store.dispatch('http/request', {
          request: addSwapToCart,
          body: this.offer,
        })
      } catch (error) {
        this.openErrorToast()
        logger.error(SWAP_ERROR_MESSAGE.ADD_SWAP, error)
      }
      this.trackingEnabled = false
      closeModal(this.modalName)
    },
    openErrorToast() {
      this.openToast({
        title: this.$t(toastTranslations.toastErrorTitle),
        message: this.$t(toastTranslations.toastErrorContent),
        variant: TOAST_VARIANTS.ERROR,
      })
    },
    openToast({ message, title, variant }) {
      this.toast = {
        ...this.toast,
        title,
        message,
        variant,
        opened: true,
        iconSrc:
          variant === TOAST_VARIANTS.SUCCESS
            ? this.$static('/img/toast/rabbit.svg')
            : this.$static('/img/toast/storm.svg'),
      }
    },
    resetFunnel() {
      this.funnel = []
      this.form = {}
      this.categories = {}
      this.step = null
      this.trackingEnabled = true
    },
    goToCategories() {
      this.step = STEPS.CATEGORIES
    },
    handleTracking({ action, label }) {
      trackSwapModal({
        category: this.datalayerCategory,
        ...this.form,
        action,
        label,
        // eslint-disable-next-line camelcase
        swap_estimation: this.offer.price || null,
      })

      trackSwapClick({
        value: {
          category: this.datalayerCategory,
          ...this.form,
          // eslint-disable-next-line camelcase
          swap_estimation: this.offer.price || null,
        },
        name: label,
        zone: 'swap',
      })
    },
  },
}
